import { SolutionMetadata } from '@/types/solution';
import { UseFormReturn } from 'react-hook-form';

/**
 * Configuration for a metadata field synchronization
 */
export interface MetadataFieldConfig {
  /** The key in the metadata object (e.g., 'name', 'shortDescription', 'tags') */
  metadataKey: keyof SolutionMetadata;
  /** The form field name */
  formFieldName: string;
  /** Optional transformation function for form value to metadata value */
  formToMetadata?: (value: any) => any;
  /** Optional transformation function for metadata value to form value */
  metadataToForm?: (value: any) => any;
}

/**
 * Auto-discovery naming conventions for form fields
 * These patterns help automatically map metadata fields to form fields
 */
const FORM_FIELD_NAMING_PATTERNS = {
  // Direct mappings (metadata key → form field name)
  // Only the most common/essential mappings to reduce maintenance
  direct: {
    'name': 'solutionTitle',           // metadata.name → form.solutionTitle
    'shortDescription': 'solutionShortDescription',
    'category': 'solutionCategory',
    'tags': 'solutionTags',
    'imageUrl': 'imageUrl',
  },
  
  // Prefix patterns (metadata key → solution + PascalCase)
  // e.g., 'version' → 'solutionVersion'
  prefixPattern: 'solution',
  
  // Common suffixes to try
  suffixes: ['', 'Field', 'Input', 'Value']
} as const;

/**
 * Special field transformations for specific data types
 */
const FIELD_TRANSFORMATIONS = {
  tags: {
    formToMetadata: (value: string) => 
      value ? value.split(',').map(tag => tag.trim()).filter(Boolean) : [],
    metadataToForm: (value: string[]) => 
      Array.isArray(value) ? value.join(', ') : '',
  },
  // Add more transformations as needed for other special field types
  // arrays: { ... },
  // dates: { ... },
  // urls: { ... }
} as const;

/**
 * Type guard to check if a key is valid for SolutionMetadata
 * This uses TypeScript's type system to validate keys
 * UPDATE THIS when you add new fields to SolutionMetadata interface
 */
function isValidMetadataKey(key: string): key is keyof SolutionMetadata {
  // This list should match the SolutionMetadata interface keys
  // This is the ONLY place that needs manual updates when adding new metadata fields
  const validKeys: (keyof SolutionMetadata)[] = [
    'name', 'shortDescription', 'category', 'tags', 'imageUrl'
  ];
  return validKeys.includes(key as keyof SolutionMetadata);
}

/**
 * Extracts metadata keys from existing JSON content or creates minimal set
 * This is TRULY future-proof - automatically discovers fields from runtime data
 */
function extractMetadataKeysFromRuntime(
  sampleMetadata?: Partial<SolutionMetadata>,
  jsonContent?: string
): (keyof SolutionMetadata)[] {
  const metadataKeys = new Set<string>();
  
  // 1. Extract from provided sample metadata (if any)
  if (sampleMetadata) {
    Object.keys(sampleMetadata).forEach(key => metadataKeys.add(key));
  }
  
  // 2. Extract from existing JSON content (if any)
  if (jsonContent) {
    try {
      const parsed = JSON.parse(jsonContent);
      if (parsed.metadata && typeof parsed.metadata === 'object') {
        Object.keys(parsed.metadata).forEach(key => metadataKeys.add(key));
      }
    } catch (error) {
      // Invalid JSON, skip
    }
  }
  
  // 3. Filter to only valid SolutionMetadata keys
  const validKeys: (keyof SolutionMetadata)[] = [];
  
  metadataKeys.forEach(key => {
    if (isValidMetadataKey(key)) {
      validKeys.push(key as keyof SolutionMetadata);
    }
  });
  
  // If no keys found, use the minimal known set from interface
  if (validKeys.length === 0) {
    return ['name', 'shortDescription', 'category', 'tags', 'imageUrl'];
  }
  
  return validKeys;
}

/**
 * TRULY future-proof auto-discovery that uses runtime metadata extraction
 * Only requires updating the SolutionMetadata interface to add new fields
 */
export function autoDiscoverMetadataFields(
  form: UseFormReturn<any>,
  sampleMetadata?: Partial<SolutionMetadata>
): MetadataFieldConfig[] {
  const configs: MetadataFieldConfig[] = [];
  const formFieldNames = Object.keys(form.getValues());
  
  // Get the current JSON content to extract existing metadata keys
  const jsonContent = form.getValues('jsonContent');
  
  // Extract metadata keys from runtime data
  const metadataKeys = extractMetadataKeysFromRuntime(sampleMetadata, jsonContent);
  
  // For each discovered metadata key, try to find a matching form field
  metadataKeys.forEach(metadataKey => {
    const formFieldName = findMatchingFormField(metadataKey, formFieldNames);
    
    if (formFieldName) {
      const config: MetadataFieldConfig = {
        metadataKey,
        formFieldName,
      };
      
      // Add transformations for special field types
      const transformations = FIELD_TRANSFORMATIONS[metadataKey as keyof typeof FIELD_TRANSFORMATIONS];
      if (transformations) {
        config.formToMetadata = transformations.formToMetadata;
        config.metadataToForm = transformations.metadataToForm;
      }
      
      configs.push(config);
    }
  });
  
  return configs;
}

/**
 * Finds a matching form field name for a given metadata key
 */
function findMatchingFormField(metadataKey: string, formFieldNames: string[]): string | null {
  // 1. Try direct mapping first
  const directMapping = FORM_FIELD_NAMING_PATTERNS.direct[metadataKey as keyof typeof FORM_FIELD_NAMING_PATTERNS.direct];
  if (directMapping && formFieldNames.includes(directMapping)) {
    return directMapping;
  }
  
  // 2. Try prefix pattern with different cases
  const prefixPattern = FORM_FIELD_NAMING_PATTERNS.prefixPattern;
  const variations = [
    `${prefixPattern}${capitalizeFirst(metadataKey)}`,  // solutionName
    `${prefixPattern}${metadataKey}`,                   // solutionname
    `${prefixPattern}${metadataKey.toUpperCase()}`,     // solutionNAME
  ];
  
  for (const variation of variations) {
    if (formFieldNames.includes(variation)) {
      return variation;
    }
  }
  
  // 3. Try with suffixes
  for (const suffix of FORM_FIELD_NAMING_PATTERNS.suffixes) {
    const withSuffix = `${prefixPattern}${capitalizeFirst(metadataKey)}${suffix}`;
    if (formFieldNames.includes(withSuffix)) {
      return withSuffix;
    }
  }
  
  // 4. Try exact match (case-insensitive)
  const exactMatch = formFieldNames.find(fieldName => 
    fieldName.toLowerCase() === metadataKey.toLowerCase()
  );
  if (exactMatch) {
    return exactMatch;
  }
  
  // 5. Try partial match (field name contains metadata key)
  const partialMatch = formFieldNames.find(fieldName => 
    fieldName.toLowerCase().includes(metadataKey.toLowerCase())
  );
  if (partialMatch) {
    return partialMatch;
  }
  
  return null;
}

/**
 * Capitalizes the first letter of a string
 */
function capitalizeFirst(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Predefined configurations for common metadata fields (for backward compatibility)
 * These are now auto-generated but kept for explicit overrides if needed
 */
export const METADATA_FIELD_CONFIGS = {
  name: {
    metadataKey: 'name' as const,
    formFieldName: 'solutionTitle',
  },
  shortDescription: {
    metadataKey: 'shortDescription' as const,
    formFieldName: 'solutionShortDescription',
  },
  tags: {
    metadataKey: 'tags' as const,
    formFieldName: 'solutionTags',
    formToMetadata: (value: string) => 
      value ? value.split(',').map(tag => tag.trim()).filter(Boolean) : [],
    metadataToForm: (value: string[]) => 
      Array.isArray(value) ? value.join(', ') : '',
  },
  imageUrl: {
    metadataKey: 'imageUrl' as const,
    formFieldName: 'imageUrl',
  },
} as const;

/**
 * Updates JSON content with form field values using auto-discovered or provided configurations
 */
export function updateJsonWithFormFields(
  jsonContent: string,
  formValues: Record<string, any>,
  fieldConfigs?: MetadataFieldConfig[]
): string | null {
  if (!jsonContent || !jsonContent.trim()) {
    return null;
  }

  try {
    const parsedContent = JSON.parse(jsonContent);
    
    // Use provided configs or auto-discover them
    const configs = fieldConfigs || autoDiscoverMetadataFields({ getValues: () => formValues } as any);
    
    // Build updated metadata object
    const updatedMetadata: Partial<SolutionMetadata> = { ...parsedContent.metadata };
    
    configs.forEach(config => {
      const formValue = formValues[config.formFieldName];
      const existingValue = parsedContent.metadata?.[config.metadataKey];
      
      // Transform form value if transformer provided
      const transformedValue = config.formToMetadata 
        ? config.formToMetadata(formValue)
        : formValue;
      
      // Preserve existing values when form is empty (following AddSolutionDialog pattern)
      if (transformedValue !== undefined && transformedValue !== '' && 
          (Array.isArray(transformedValue) ? transformedValue.length > 0 : true)) {
        updatedMetadata[config.metadataKey] = transformedValue;
      } else if (existingValue) {
        // Keep existing value if form value is empty
        updatedMetadata[config.metadataKey] = existingValue;
      }
    });
    
    const updatedContent = {
      ...parsedContent,
      metadata: updatedMetadata
    };
    
    return JSON.stringify(updatedContent, null, 2);
  } catch (error) {
    console.error('JSON parsing error during metadata update:', error);
    return null;
  }
}

/**
 * Extracts form field values from JSON metadata using auto-discovered or provided configurations
 */
export function extractFormValuesFromJson(
  jsonContent: string,
  fieldConfigs?: MetadataFieldConfig[]
): Record<string, any> | null {
  if (!jsonContent || !jsonContent.trim()) {
    return null;
  }

  try {
    const parsedContent = JSON.parse(jsonContent);
    const metadata = parsedContent.metadata;
    
    if (!metadata) {
      return null;
    }
    
    // Use provided configs or auto-discover them (we need a form reference for auto-discovery)
    const configs = fieldConfigs || [];
    if (configs.length === 0) {
      console.warn('No field configs provided and cannot auto-discover without form reference');
      return null;
    }
    
    const formValues: Record<string, any> = {};
    
    configs.forEach(config => {
      const metadataValue = metadata[config.metadataKey];
      
      if (metadataValue !== undefined && metadataValue !== null) {
        // Transform metadata value if transformer provided
        formValues[config.formFieldName] = config.metadataToForm 
          ? config.metadataToForm(metadataValue)
          : metadataValue;
      }
    });
    
    return formValues;
  } catch (error) {
    console.log('Could not parse JSON for form value extraction');
    return null;
  }
}

/**
 * Checks if two values are different for synchronization purposes
 * Handles arrays, strings, and other primitive types
 */
export function areValuesDifferent(value1: any, value2: any): boolean {
  // Handle array comparison (for tags)
  if (Array.isArray(value1) && Array.isArray(value2)) {
    return JSON.stringify(value1) !== JSON.stringify(value2);
  }
  
  // Handle string comparison
  if (typeof value1 === 'string' && typeof value2 === 'string') {
    return value1.trim() !== value2.trim();
  }
  
  // Handle other types
  return value1 !== value2;
}

/**
 * Validates that a metadata field configuration is properly set up
 */
export function validateFieldConfig(config: MetadataFieldConfig): boolean {
  return !!(config.metadataKey && config.formFieldName);
} 